import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  gender: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  status: string;
  message: string;
  data: {
    user: User;
    token?: string;
    requiresVerification?: boolean;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials {
  email: string;
  password: string;
  name: string;
  phoneNumber: string;
  gender: string;
  role?: string;
  studentId?: string;
  emergencyContactName: string;
  emergencyContactNumber: string;
}

export interface ForgotPasswordCredentials {
  email: string;
}

export interface ResetPasswordCredentials {
  password: string;
}

class AuthService {
  async signup(credentials: SignupCredentials): Promise<AuthResponse> {
    try {
      // Student ID is optional — omit it entirely when blank so we never store
      // an empty string on the user record.
      const payload = { ...credentials };
      if (!payload.studentId?.trim()) {
        delete payload.studentId;
      }
      const response = await apiClient.post<AuthResponse>(
        API_ENDPOINTS.AUTH.SIGNUP,
        payload
      );

      // Auth is cookie-based; nothing to persist client-side. Unverified users
      // receive no session cookie until they verify their email.
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      // The server sets an httpOnly auth cookie; there is no token to store here.
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<{ status: string; data: { user: User } }>(
        API_ENDPOINTS.USERS.GET_PROFILE
      );

      return response.data.data.user;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  logout(): void {
    // Clearing the httpOnly cookie is the server's job — fire the logout
    // endpoint and ignore the outcome (the user is logged out locally regardless).
    apiClient.post(API_ENDPOINTS.AUTH.LOGOUT).catch(() => {
      // Silently handle logout errors - user is logged out locally anyway
    });
  }

  async forgotPassword(credentials: ForgotPasswordCredentials): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        credentials
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async resetPassword(token: string, credentials: ResetPasswordCredentials): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        API_ENDPOINTS.AUTH.RESET_PASSWORD,
        { token, ...credentials }
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  async resendVerificationEmail(email: string): Promise<{ status: string; message: string }> {
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        API_ENDPOINTS.AUTH.RESEND_VERIFICATION,
        { email }
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const authService = new AuthService();
export default authService;